import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { TotpEnrollment } from "../components/security/TotpEnrollment";
import { useAsyncCallback } from "../hooks/useAsyncCallback";
import { useEffect, useRef, useState } from "react";
import { Button } from "@components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@components/ui/card";
import { PageHeader } from "@components/layout/PageHeader";
import { PageShell } from "@components/layout/PageShell";
import { toast } from "sonner";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@components/ui/breadcrumb";
import { SidebarTrigger } from "@components/ui/sidebar";
import {
  confirmTotpSetup,
  disableTotp,
  getMfaStatus,
  regenerateRecoveryCodes,
  startTotpSetup,
} from "../lib/auth";

type SecurityPageProps = {
  navigate: (route: string) => void;
};

const preferredMethods: Record<string, string> = { sms: "SMS", totp: "Authenticator app" };

export function SecurityPage({ navigate }: SecurityPageProps) {
  const [status, setStatus] = useState<{ enabled: boolean; preferred?: string; emailSignInReady?: boolean; setupState?: string; setupId?: string } | null>(
    null
  );
  const [setupSecret, setSetupSecret] = useState("");
  const [setupUri, setSetupUri] = useState("");
  const [setupCode, setSetupCode] = useState("");
  const [setupId, setSetupId] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const generation = useRef(0);
  const mounted = useRef(true);
  const current = (version: number) => mounted.current && generation.current === version;

  const preferredLabel = preferredMethods[status?.preferred ?? ""] ?? "software token";

  const breadcrumb = (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <button type="button" onClick={() => navigate("/dashboard")}>
              Dashboard
            </button>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>Security</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );

  const loadStatus = async () => {
    const version = generation.current;
    try {
      const data = await getMfaStatus();
      if (!current(version)) return;
      setStatus(data);
      if (data.setupId) setSetupId(data.setupId);
    } catch (err) {
      if (!current(version)) return;
      const message =
        err instanceof Error ? err.message : "Unable to load 2FA status.";
      setError(message);
    }
  };

  useEffect(() => {
    mounted.current = true;
    const clearPrincipalState = () => {
      generation.current += 1;
      setStatus(null); setSetupId(""); setSetupSecret(""); setSetupUri("");
      setSetupCode(""); setRecoveryCodes([]); setError(null); setIsSubmitting(false);
      void loadStatus();
    };
    void loadStatus();
    window.addEventListener("auth:changed", clearPrincipalState);
    return () => {
      mounted.current = false;
      generation.current += 1;
      window.removeEventListener("auth:changed", clearPrincipalState);
    };
  }, []);

  const {
    pending: disableTwoFactorPending,
    callback: disableTwoFactor,
  } = useAsyncCallback(async () => {
    const version = ++generation.current;
    setIsSubmitting(true);
    setError(null);
    try {
      await disableTotp();
      if (!current(version)) return;
      toast.success("Two-factor authentication disabled.");
      setSetupSecret("");
      setSetupUri("");
      setSetupCode("");
      setRecoveryCodes([]);
      await loadStatus();
    } catch (err) {
      if (!current(version)) return;
      const message =
        err instanceof Error
          ? err.message
          : "Unable to disable 2FA.";
      toast.error(message);
    } finally {
      if (current(version)) setIsSubmitting(false);
    }
  });
  const {
    pending: beginTwoFactorPending,
    callback: beginTwoFactor,
  } = useAsyncCallback(async () => {
    const version = ++generation.current;
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await startTotpSetup();
      if (!current(version)) return;
      setSetupSecret(response.secret);
      setSetupUri(response.otpauth);
      setSetupId(response.setupId);
    } catch (err) {
      if (!current(version)) return;
      const message =
        err instanceof Error
          ? err.message
          : "Unable to start 2FA setup.";
      toast.error(message);
    } finally {
      if (current(version)) setIsSubmitting(false);
    }
  });
  const {
    pending: confirmTwoFactorPending,
    callback: confirmTwoFactor,
  } = useAsyncCallback(async () => {
    const version = ++generation.current;
    setIsSubmitting(true);
    setError(null);
    let enrollmentConfirmed = false;
    try {
      await confirmTotpSetup({ code: setupCode.trim(), setupId });
      if (!current(version)) return;
      enrollmentConfirmed = true;
      setSetupSecret("");
      setSetupUri("");
      setSetupCode("");
      setStatus({ enabled: true, preferred: "totp", emailSignInReady: true, setupState: "ready" });
      toast.success("Two-factor authentication enabled.");
      const codes = await regenerateRecoveryCodes();
      if (!current(version)) return;
      setRecoveryCodes(codes);
    } catch (err) {
      if (!current(version)) return;
      let message = err instanceof Error ? err.message : "Unable to confirm 2FA setup.";
      if (enrollmentConfirmed) {
        message = "Two-factor authentication is enabled, but recovery codes could not be generated. Try Generate recovery codes again.";
      }
      setError(message);
      toast.error(message);
    } finally {
      if (enrollmentConfirmed && current(version)) await loadStatus();
      if (current(version)) setIsSubmitting(false);
    }
  });
  const {
    pending: generateRecoveryPending,
    callback: generateRecovery,
  } = useAsyncCallback(async () => {
    const version = ++generation.current;
    setIsSubmitting(true);
    setError(null);
    try {
      const codes = await regenerateRecoveryCodes();
      if (!current(version)) return;
      setRecoveryCodes(codes);
      toast.success("New recovery codes generated.");
    } catch (err) {
      if (!current(version)) return;
      const message =
        err instanceof Error
          ? err.message
          : "Unable to generate recovery codes.";
      toast.error(message);
    } finally {
      if (current(version)) setIsSubmitting(false);
    }
  });

  return (
    <PageShell maxWidth="6xl">
      <PageHeader
        breadcrumb={breadcrumb}
        leading={<SidebarTrigger />}
        eyebrow="Security Center"
        title="Security"
        description="Manage two-factor authentication for your account."
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              Back to dashboard
            </Button>
          </>
        }
      />

      <section className="mt-10 grid gap-6 md:grid-cols-2">
        <Card className="border-border bg-card/60">
          <CardHeader>
            <CardTitle className="text-lg">Two-factor authentication</CardTitle>
            <CardDescription>
              One authenticator entry for password and email-link sign-in.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-md border border-border/60 bg-muted/40 px-4 py-3 text-sm">
              <p className="font-medium text-foreground/90">
                Status: {status?.enabled ? "Enabled" : "Not enabled"}
              </p>
              {status?.enabled ? (
              <p className="mt-1 text-xs text-muted-foreground">
                Preferred method: {preferredLabel}
              </p>
            ) : null}
            </div>

            {status?.enabled && !status.emailSignInReady ? (
              <p className="text-sm text-muted-foreground">Update your authenticator once to use the same entry for email-link sign-in. Sign in with your password and current MFA code first, then finish setup within five minutes.</p>
            ) : null}
            {status?.enabled ? (
              <div className="flex flex-wrap gap-2">
              <Button loading={beginTwoFactorPending} loadingLabel="Starting setup…" type="button" variant="outline" disabled={!status || isSubmitting} onClick={() => void beginTwoFactor()}>Update authenticator</Button>
              <Button loading={disableTwoFactorPending} loadingLabel="Disabling 2FA…"
                type="button"
                variant="outline"
                disabled={!status || isSubmitting}
                onClick={() => void disableTwoFactor()}
              >
                Disable 2FA
              </Button>
              </div>
            ) : (
              <Button loading={beginTwoFactorPending} loadingLabel="Starting setup…"
                type="button"
                disabled={!status || isSubmitting}
                onClick={() => void beginTwoFactor()}
              >
                Start 2FA setup
              </Button>
            )}

            {!setupSecret && status?.setupState === "synchronizing" && setupId ? (
              <form className="space-y-3" onSubmit={(event) => { event.preventDefault(); void confirmTwoFactor(); }}>
                <p className="text-sm text-muted-foreground">Finish setup using the new entry you added to your authenticator.</p>
                <Label htmlFor="resume-authenticator-code">Authenticator code</Label>
                <Input id="resume-authenticator-code" inputMode="numeric" autoComplete="one-time-code" value={setupCode} onChange={(event) => setSetupCode(event.target.value)} disabled={!status || isSubmitting} />
                <Button className="h-auto min-h-11 max-w-full whitespace-normal" type="submit" disabled={isSubmitting || !setupCode.trim()} loading={confirmTwoFactorPending} loadingLabel="Finishing setup…">Finish authenticator setup</Button>
              </form>
            ) : null}

            {setupSecret ? (
              <TotpEnrollment secret={setupSecret} uri={setupUri} code={setupCode} onCodeChange={setSetupCode} onConfirm={() => void confirmTwoFactor()} confirming={confirmTwoFactorPending} disabled={!status || isSubmitting} />
            ) : null}

            {error ? (
              <p className="text-sm text-red-400" role="alert">
                {error}
              </p>
            ) : null}
          </CardContent>
        </Card>


        <Card className="border-border bg-card/60">
          <CardHeader>
            <CardTitle className="text-lg">Recovery codes</CardTitle>
            <CardDescription>
              Save these one-time codes in a secure place for account recovery.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button loading={generateRecoveryPending} loadingLabel="Generating codes…"
              type="button"
              variant="outline"
              disabled={!status?.enabled || isSubmitting}
              onClick={() => void generateRecovery()}
            >
              {recoveryCodes.length > 0 ? "Regenerate recovery codes" : "Generate recovery codes"}
            </Button>
            {recoveryCodes.length > 0 ? (
              <div className="grid gap-2 rounded-md border border-border/60 bg-muted/40 p-4 text-sm">
                {recoveryCodes.map((code) => (
                  <div key={code} className="font-mono tracking-wide text-foreground/90">
                    {code}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Recovery codes are shown once. Regenerating codes invalidates any
                previously saved codes.
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="border-border bg-card/60">
          <CardHeader>
            <CardTitle className="text-lg">Recovery guidance</CardTitle>
            <CardDescription>
              Use these steps if you lose access to your authenticator app.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              If you still have access to your account, disable 2FA here and re-enable
              it after setting up a new authenticator device.
            </p>
            <p>
              If you are locked out, contact support so we can verify identity and
              reset MFA on your behalf.
            </p>
          </CardContent>
        </Card>
      </section>
    </PageShell>
  );
}
